import { Segment } from '@mi-front/core';
import { segmentsGetSegmentsRangeQueryKey, useSegmentsGetSegmentsRange } from '@mi-front/api-v2';
import debugFn from 'debug';

const debug = debugFn('segments-api');

type UseApiSegmentsRangeOptions = NonNullable<
  Parameters<typeof useSegmentsGetSegmentsRange<Segment[]>>[2]
>['query'];

/**
 * Logs segment range fetch results for the logger stream.
 */
function selectSegmentsRange(segments: Segment[]): Segment[] {
  debug(
    'Fetched %d segments from %s to %s',
    segments.length,
    segments[0]?.startTs ? new Date(segments[0].startTs).toLocaleTimeString() : 'N/A',
    segments[segments.length - 1]?.endTs
      ? new Date(segments[segments.length - 1].endTs).toLocaleTimeString()
      : 'N/A',
  );

  return segments;
}

/**
 * React Query wrapper around api-v2 segments range endpoint.
 */
export function useApiSegmentsRange(
  stationId: string,
  from: string | number,
  to: string | number,
  options?: UseApiSegmentsRangeOptions,
) {
  const isoFrom = new Date(from).toISOString();
  const isoTo = new Date(to).toISOString();

  // If the range is in the future, do not cache data more than 30 seconds.
  // Otherwise cache for 1 hour.
  const staleTime = new Date(to) >= new Date() ? 30 * 1000 : 60 * 60 * 1000;

  return useSegmentsGetSegmentsRange<Segment[]>(
    stationId,
    { from: isoFrom, to: isoTo },
    {
      query: {
        staleTime,
        placeholderData: () => undefined,
        select: selectSegmentsRange,
        ...options,
      },
    },
  );
}

useApiSegmentsRange.queryKey = (stationId: string, from: string | number, to: string | number) =>
  segmentsGetSegmentsRangeQueryKey(stationId, {
    from: new Date(from).toISOString(),
    to: new Date(to).toISOString(),
  });
